import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { portalOrderService } from '../../services/portalOrders';
import type {
  MatchedCustomer,
  PortalOrderStatsResponse,
} from '../../services/portalOrders';
import type { OrderRecord, OrderStatus } from '../../types/orders';
import {
  ORDER_STATUS_LABELS,
  isAwaitingSageAccount,
} from '../../types/orders';
import { formatDateTime } from '../../utils/dateFormatters';
import { formatLateSyncLabel, lateSyncTitle } from '../../utils/lateSync';
import { formatMoney } from '../../utils/formatting';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import Toast from '../../components/portal/Toast';
import OrderDetailModal from '../../components/portal/OrderDetailModal';
import CreateOrderDialog from '../../components/portal/CreateOrderDialog';
import DeletedInSageBadge from '../../components/portal/DeletedInSageBadge';
import ConfidenceBadge from '../../components/portal/ConfidenceBadge';
import ParkedBadge from '../../components/portal/ParkedBadge';
import AwaitingSageAccountBadge from '../../components/portal/AwaitingSageAccountBadge';
import ItemsNowInSageBadge from '../../components/portal/ItemsNowInSageBadge';
import DueBadge from '../../components/portal/DueBadge';
import OrderNumberChip from '../../components/portal/OrderNumberChip';
import UnpricedLinesMarker from '../../components/portal/UnpricedLinesMarker';
import { countDue, hoistDueDrafts, isDue, dueRowAccent } from './dueDrafts';
import OrdersBulkBar from '../../components/portal/OrdersBulkBar';
import AiDraftingBanner from '../../components/portal/AiDraftingBanner';
import { trackEvent } from '../../services/portalTracking';
import { useListNavigation, type NavigationTarget } from '../../hooks/useListNavigation';
import {
  BARE_PREFIX_HINT,
  ORDER_SEARCH_PLACEHOLDER,
  isBareOrderNumberPrefix,
} from './orderSearchHint';
import { useToast } from '../../hooks/useToast';
import {
  toggleSelection,
  toggleSelectAll,
  selectAllState,
  pruneSelection,
  countDraftSelected,
  selectedDraftIds,
} from './portalOrdersBulkUtils';
import {
  isSortDir,
  isSortKey,
  nextSortState,
  type OrdersSortDir,
  type OrdersSortKey,
} from './portalOrdersSort';
import {
  readRememberedOrderFilter,
  rememberOrderFilter,
  resolveInitialOrderFilter,
} from './orderFilterMemory';
import type { OrderFilter, OrderFilterSource } from './orderFilterMemory';
import styles from './PortalOrdersPage.module.css';

// Filter for order status. ``on_hold`` is a special pseudo-filter that
// matches orders with a non-NULL ``cancel_reason_code`` (any Sage hold).
// It maps to the backend ``hold_reason=any`` query param rather than a
// status; the backend does not require the orders to be in any particular
// status to be on hold. The type, its values and the per-person memory of
// the last chosen filter live in ./orderFilterMemory.ts.
/** Rows per list request; next/previous appends another page at the end. */
const ORDERS_PAGE_SIZE = 100;

const ORDER_FILTER_LABELS: Record<OrderFilter, string> = {
  all: 'All',
  draft: 'Draft',
  submission_failed: 'Failed',
  submitted_to_sage: 'Submitted to Sage',
  confirmed_in_sage: 'Confirmed in Sage',
  accepted: 'Accepted',
  shipped: 'Shipped',
  order_completed: 'Completed',
  active: 'Active',
  on_hold: 'On hold',
};


function SortIcon({ active, direction }: { active: boolean; direction: OrdersSortDir }) {
  if (!active) {
    return <span className={styles.sortIcon} aria-hidden="true">{'\u2195'}</span>;
  }
  return (
    <span className={`${styles.sortIcon} ${styles.sortIconActive}`} aria-hidden="true">
      {direction === 'asc' ? '\u2191' : '\u2193'}
    </span>
  );
}

const PortalOrdersPage: React.FC = () => {
  // === URL-driven state (deep-linking) ===
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkOrderId = searchParams.get('orderId');
  const navigate = useNavigate();
  // orders:read viewers see the list read-only: no create, no bulk actions.
  const { hasScope, user } = useAuth();
  const canManageOrders = hasScope('orders:manage');
  const userId = user?.id ?? null;

  // Which filter the page opens on: an explicit ?filter= wins, then an
  // order deep link (full list), then the filter this person last chose
  // (remembered per user), then All. Resolved once, at mount.
  const [initialFilterState] = useState<{ filter: OrderFilter; source: OrderFilterSource }>(() =>
    resolveInitialOrderFilter({
      urlFilter: searchParams.get('filter'),
      urlOrderId: deepLinkOrderId,
      remembered: readRememberedOrderFilter(userId),
    }),
  );
  const initialFilter: OrderFilter = initialFilterState.filter;
  // How the current filter came to be active. Shown beside it while it is
  // restored, so a remembered filter is never mistaken for "the page always
  // opens like this"; becomes 'chosen' at the first click.
  const [filterSource, setFilterSource] = useState<OrderFilterSource | 'chosen'>(
    initialFilterState.source,
  );
  const initialSearch: string = searchParams.get('search') ?? '';
  const initialSortBy: OrdersSortKey = isSortKey(searchParams.get('sort_by'))
    ? (searchParams.get('sort_by') as OrdersSortKey)
    : 'created_at';
  const initialSortDir: OrdersSortDir = isSortDir(searchParams.get('sort_order'))
    ? (searchParams.get('sort_order') as OrdersSortDir)
    : 'desc';

  // === Orders state ===
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [stats, setStats] = useState<PortalOrderStatsResponse | null>(null);
  const [orderFilter, setOrderFilter] = useState<OrderFilter>(initialFilter);
  // Client-side sub-filter on the Draft tab: unresolved-review drafts
  // ("Needs attention", attention_count > 0) vs clean drafts ("Ready for
  // Sage", attention_count === 0). Null = no sub-filter.
  const [draftAttentionFilter, setDraftAttentionFilter] =
    useState<'needs_attention' | 'ready' | 'parked' | 'due' | null>(null);

  // === Search state (debounced) ===
  const [searchInput, setSearchInput] = useState<string>(initialSearch);
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  // The one customer every match of the current search belongs to, when
  // the API could name one (kanban f33d0f2c): a one-word customer search
  // ends on the customer page 13 of 59 times, so the page links there.
  const [matchedCustomer, setMatchedCustomer] = useState<MatchedCustomer | null>(null);

  // === Sort state ===
  const [sortBy, setSortBy] = useState<OrdersSortKey>(initialSortBy);
  const [sortDir, setSortDir] = useState<OrdersSortDir>(initialSortDir);

  // === Detail state ===
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(deepLinkOrderId);
  // The bell (and any other in-app link) can change ?orderId= while this
  // page is already mounted: the CSR is usually ON Orders when a freight
  // alert lands, and the mount-time initialiser above left the modal shut
  // (found on the 2026-10-07 staging proof, kanban 614b858a). Follow the
  // param whenever it names an order other than the one open now.
  useEffect(() => {
    if (deepLinkOrderId && deepLinkOrderId !== selectedOrderId) {
      setSelectedOrderId(deepLinkOrderId);
    }
    // selectedOrderId is deliberately not a dependency: closing the modal
    // must not reopen it from a param that has not changed.
  }, [deepLinkOrderId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Closing the modal drops ?orderId= so the same link opens it again later.
  const closeOrderDetail = useCallback(() => {
    setSelectedOrderId(null);
    if (searchParams.has('orderId')) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('orderId');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // === Bulk-select state ===
  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => new Set());
  const [bulkDismissing, setBulkDismissing] = useState(false);

  // === Loading state ===
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  // A further page is being appended because Next was pressed on the last
  // loaded order (kanban 97abd058).
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  // === Toast state ===
  const { toasts, addToast, removeToast } = useToast();

  // ==========================================================================
  // URL Sync
  // ==========================================================================
  //
  // Keep the URL query string in sync with the filter/search/sort state so
  // links can deep-link to a specific view. We preserve any unrelated
  // params (e.g. orderId) by merging into the existing searchParams.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);

    if (orderFilter === 'all') next.delete('filter');
    else next.set('filter', orderFilter);

    if (debouncedSearch.trim()) next.set('search', debouncedSearch.trim());
    else next.delete('search');

    if (sortBy === 'created_at') next.delete('sort_by');
    else next.set('sort_by', sortBy);

    if (sortDir === 'desc') next.delete('sort_order');
    else next.set('sort_order', sortDir);

    // Only update when something actually changed, to avoid history churn.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [orderFilter, debouncedSearch, sortBy, sortDir, searchParams, setSearchParams]);

  // ==========================================================================
  // Fetch Functions
  // ==========================================================================

  // Last search query recorded as a portal_orders_search event, so a
  // re-fetch for a sort or filter change does not record the same query
  // twice. The event is recorded once the results are in, so it can carry
  // how many orders the query matched (kanban 699f7c24: empty-result
  // searches must be visible in portal_events).
  const lastTrackedSearchRef = useRef<string | null>(null);

  // The list query for the current filter, search and sort; the first page
  // and the next/previous load-more share it so an appended page always
  // continues the same list.
  const listParams = useCallback(
    (offset: number) => {
      const trimmed = debouncedSearch.trim();
      const statusFilter: string | undefined =
        orderFilter === 'all' ? undefined :
        orderFilter === 'active' ? undefined :
        orderFilter === 'on_hold' ? undefined :
        orderFilter;
      return {
        status: statusFilter,
        exclude_terminal: orderFilter === 'active',
        // ``on_hold`` is the on-hold pseudo-filter — translates to the
        // backend's ``hold_reason=any`` (any non-NULL Sage CancelReasonCode).
        hold_reason: orderFilter === 'on_hold' ? 'any' : undefined,
        search: trimmed || undefined,
        limit: ORDERS_PAGE_SIZE,
        offset: offset || undefined,
        sort_by: sortBy,
        sort_order: sortDir,
      };
    },
    [orderFilter, debouncedSearch, sortBy, sortDir],
  );

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    const trimmed = debouncedSearch.trim();
    try {
      const response = await portalOrderService.listOrders(listParams(0));

      const fetched = Array.isArray(response?.orders) ? response.orders : [];
      const fetchedTotal = typeof response?.total === 'number' ? response.total : fetched.length;
      setOrders(fetched);
      setTotal(fetchedTotal);
      setMatchedCustomer(trimmed ? (response?.matched_customer ?? null) : null);
      if (trimmed && lastTrackedSearchRef.current !== trimmed) {
        lastTrackedSearchRef.current = trimmed;
        trackEvent('search', 'portal_orders_search', {
          query: trimmed,
          result_count: fetchedTotal,
          filter: orderFilter,
          // The search box carries data-action="portal_orders_search" and the
          // recorder writes that row itself (kanban 68b23ba8).
          recorded_by_dom: true,
        });
      }
    } catch (err) {
      setError('Failed to load orders');
      console.error(err);
      if (trimmed && lastTrackedSearchRef.current !== trimmed) {
        lastTrackedSearchRef.current = trimmed;
        trackEvent('search', 'portal_orders_search', {
          query: trimmed,
          result_count: null,
          filter: orderFilter,
          error: 'fetch_failed',
          recorded_by_dom: true,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [orderFilter, debouncedSearch, listParams]);

  // Append the next page of the same list (next/previous at the end of the
  // loaded rows). Rows already loaded are skipped so a list that shifted
  // under us never shows an order twice.
  const loadMoreOrders = useCallback(async () => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const response = await portalOrderService.listOrders(listParams(orders.length));
      const fetched = Array.isArray(response?.orders) ? response.orders : [];
      setOrders((prev) => {
        const seen = new Set(prev.map((o) => o.id));
        return [...prev, ...fetched.filter((o) => !seen.has(o.id))];
      });
      if (typeof response?.total === 'number') setTotal(response.total);
    } catch (err) {
      console.error('Failed to load more orders', err);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [listParams, orders.length]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await portalOrderService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats', err);
    }
  }, []);

  // ==========================================================================
  // Effects
  // ==========================================================================

  // A restored filter is applied without a click, so record it under the
  // same event as a click with source=restored: the usage graph counts
  // "opened on Draft" either way and can still tell the two apart.
  useEffect(() => {
    if (initialFilterState.source === 'restored') {
      trackEvent('filter', 'order_status_filter', {
        filter: initialFilterState.filter,
        source: 'restored',
      });
    }
    // Mount-only: initialFilterState never changes after the first render.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch data on mount and when filter/search/sort changes.
  // Preserve deep-link orderId on initial mount; reset on subsequent changes.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current && deepLinkOrderId) {
      isInitialMount.current = false;
    } else {
      // Don't clear the selection on every re-fetch caused by search typing;
      // the modal stays open even as the list behind it refreshes. We only
      // close it when the *filter* changes, which is handled in the tab
      // click handler directly.
    }
    fetchOrders();
  }, [fetchOrders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stats refresh on mount only (counts don't depend on search/sort).
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ==========================================================================
  // Handlers
  // ==========================================================================

  const handleSelectOrder = (orderId: string) => {
    trackEvent('click', 'order_open', { order_id: orderId });
    setSelectedOrderId(orderId === selectedOrderId ? null : orderId);
  };

  // The secondary "Customer" link on an order row. Its own named action
  // (not order_open) so the orders -> customer -> back-within-20s bounces
  // measured before this change (18 of 65 in 90 days, card 9f6660cc) can
  // be compared with deliberate customer visits after it.
  const handleOpenCustomer = (e: React.MouseEvent<HTMLAnchorElement>, order: OrderRecord) => {
    e.stopPropagation();
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      // Let the browser handle modified clicks (new tab) on the real href.
      return;
    }
    e.preventDefault();
    trackEvent('click', 'orders_list_customer_link', {
      order_id: order.id,
      customer_no: order.customer_no,
    });
    navigate(`/portal/customers/${order.customer_no}`);
  };

  // The "open customer page" link above a search whose matches all belong
  // to one customer. Its own named action so the usage graph can tell it
  // from the per-row link and from a customer_open on the Customers page.
  const handleOpenMatchedCustomer = (e: React.MouseEvent<HTMLAnchorElement>, customer: MatchedCustomer) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    trackEvent('click', 'orders_search_customer_link', {
      customer_no: customer.customer_no,
      query: debouncedSearch.trim(),
      result_count: total,
    });
    navigate(`/portal/customers/${customer.customer_no}`);
  };

  const handleRefresh = () => {
    fetchOrders();
    fetchStats();
  };

  const handleOrderCreated = (orderId: string) => {
    trackEvent('action', 'order_created', { order_id: orderId, source: 'manual' });
    // Close create dialog and open OrderDetailModal for line-item editing.
    setShowCreateDialog(false);
    setSelectedOrderId(orderId);
    fetchOrders();
    fetchStats();
  };

  const handleFilterClick = (filter: OrderFilter) => {
    trackEvent('filter', 'order_status_filter', { filter, source: 'chosen' });
    rememberOrderFilter(userId, filter);
    setFilterSource('chosen');
    setOrderFilter(filter);
    setSelectedOrderId(null);
    // The attention sub-filter only applies to the Draft tab.
    setDraftAttentionFilter(null);
  };

  const handleAttentionFilterClick = (filter: 'needs_attention' | 'ready' | 'parked' | 'due') => {
    trackEvent('filter', 'order_attention_filter', { filter });
    setDraftAttentionFilter((prev) => (prev === filter ? null : filter));
  };

  const handleSortClick = (key: OrdersSortKey) => {
    trackEvent('filter', 'portal_orders_sort', { sort_by: key });
    const next = nextSortState({ sortBy, sortDir }, key);
    setSortBy(next.sortBy);
    setSortDir(next.sortDir);
  };

  // ==========================================================================
  // Bulk-select handlers
  // ==========================================================================

  // Client-side attention sub-filter (Draft tab only). Rows without an
  // enriched attention_count match neither pill — we can't classify them.
  const visibleOrders = useMemo(() => {
    // Drafts whose customer-requested ship / pickup date is overdue, due
    // today or due next business day rise to the top of the Draft tab
    // whatever the sort (kanban 3d77f755) — a same-day pickup must never
    // sit in created_at order under routine drafts.
    const hoisted = orderFilter === 'draft' ? hoistDueDrafts(orders) : orders;
    if (orderFilter !== 'draft' || !draftAttentionFilter) return hoisted;
    // "Parked" is orthogonal to review state: the desk left the draft
    // un-keyed on purpose (2026-09-24 order portal review).
    if (draftAttentionFilter === 'parked') return hoisted.filter((o) => !!o.parked_reason);
    if (draftAttentionFilter === 'due') return hoisted.filter(isDue);
    return hoisted.filter((o) => {
      if (o.attention_count == null) return false;
      return draftAttentionFilter === 'needs_attention'
        ? o.attention_count > 0
        : o.attention_count === 0;
    });
  }, [orders, orderFilter, draftAttentionFilter]);

  const visibleIds = useMemo(() => visibleOrders.map((o) => o.id), [visibleOrders]);

  // Next / previous while an order is open (kanban 97abd058): moves through
  // the rows as shown (filter, attention sub-filter, sort, due hoisting),
  // fetching the next page when Next is pressed on the last loaded row.
  // Recorded as order_next / order_prev, not order_open, so the usage graph
  // can measure how much of the open-then-open-again pattern now goes
  // through it.
  const handleNavigate = useCallback(
    (target: NavigationTarget) => {
      trackEvent('click', `order_${target.direction}`, {
        order_id: target.id,
        from_order_id: target.fromId,
        via: target.via,
        index: target.index,
        loaded_more: target.loadedMore,
      });
      setSelectedOrderId(target.id);
      if (searchParams.has('orderId')) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('orderId', target.id);
          return next;
        }, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );
  const loadMoreForNavigation = useCallback(() => { void loadMoreOrders(); }, [loadMoreOrders]);
  const orderNavigation = useListNavigation({
    ids: visibleIds,
    currentId: selectedOrderId,
    total,
    hasMore: orders.length < total,
    loadingMore,
    loadMore: loadMoreForNavigation,
    onNavigate: handleNavigate,
  });
  const dueCount = useMemo(() => countDue(orders), [orders]);
  const draftSelectedCount = useMemo(
    () => countDraftSelected(checkedIds, orders),
    [checkedIds, orders],
  );
  const headerCheckState = useMemo(
    () => selectAllState(checkedIds, visibleIds),
    [checkedIds, visibleIds],
  );

  // After every list refresh, drop selection ids that are no longer
  // visible — keeps the bulk-bar count consistent with what the user
  // can actually see.
  useEffect(() => {
    setCheckedIds((prev) => {
      const pruned = pruneSelection(prev, visibleIds);
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleIds]);

  const handleToggleRow = (orderId: string) => {
    setCheckedIds((prev) => toggleSelection(prev, orderId));
  };

  const handleToggleAll = () => {
    setCheckedIds((prev) => toggleSelectAll(prev, visibleIds));
  };

  const handleClearSelection = () => {
    setCheckedIds(new Set());
  };

  const handleBulkDismissDrafts = async () => {
    const ids = selectedDraftIds(checkedIds, orders);
    if (ids.length === 0) return;
    const ok = window.confirm(
      `Permanently dismiss ${ids.length} draft order${ids.length === 1 ? '' : 's'}? ` +
        `This cannot be undone.`,
    );
    if (!ok) return;

    setBulkDismissing(true);
    try {
      const result = await portalOrderService.bulkDismissDrafts(ids);
      const blocked = result.skipped_details.filter((d) => d.reason === 'blocked');
      trackEvent('action', 'orders_bulk_dismiss', {
        requested: ids.length,
        dismissed: result.dismissed,
        skipped: result.skipped,
        blocked: blocked.length,
      });
      const skippedNote =
        result.skipped > 0 ? ` (${result.skipped} skipped)` : '';
      // A blocked draft is one the database refused to delete (a linked
      // record still points at it): say why, not just that it was skipped.
      const blockedNote =
        blocked.length > 0 && blocked[0].detail ? ` ${blocked[0].detail}` : '';
      addToast(
        `Dismissed ${result.dismissed} draft${result.dismissed === 1 ? '' : 's'}${skippedNote}.${blockedNote}`.replace(/\.\.$/, '.'),
        result.skipped > 0 ? 'warning' : 'success',
      );
      handleClearSelection();
      fetchOrders();
      fetchStats();
    } catch (err) {
      console.error(err);
      addToast('Failed to dismiss selected drafts', 'error');
    } finally {
      setBulkDismissing(false);
    }
  };

  // ==========================================================================
  // Helpers
  // ==========================================================================

  const getStatusClass = (status: OrderStatus): string => {
    const map: Record<string, string> = {
      draft: styles.statusDraft,
      submission_failed: styles.statusCancelled,
      submitted_to_sage: styles.statusSubmitted,
      confirmed_in_sage: styles.statusConfirmed,
      accepted: styles.statusAccepted,
      shipped: styles.statusShipped,
      order_completed: styles.statusCompleted,
      cancelled: styles.statusCancelled,
    };
    return map[status] || '';
  };

  const renderSortLabel = (label: string, key: OrdersSortKey) => (
    <button
      type="button"
      className={`${styles.sortHeaderBtn} ${sortBy === key ? styles.sortHeaderBtnActive : ''}`}
      onClick={() => handleSortClick(key)}
      aria-sort={sortBy === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span>{label}</span>
      <SortIcon active={sortBy === key} direction={sortDir} />
    </button>
  );

  const searchIsActive = useMemo(() => debouncedSearch.trim().length > 0, [debouncedSearch]);

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>
          Order Review
          {!canManageOrders && (
            <span
              title="You can view orders but not edit them"
              style={{
                marginLeft: 10, verticalAlign: 'middle', fontSize: 12, fontWeight: 600,
                color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb',
                borderRadius: 999, padding: '2px 10px',
              }}
            >
              Read-only
            </span>
          )}
        </h1>
        <div className={styles.actions}>
          {canManageOrders && (
            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={() => { trackEvent('action', 'new_order_click'); setShowCreateDialog(true); }}
              type="button"
              data-action="new_order_click"
              data-outcome="open"
            >
              + New Order
            </button>
          )}
          <button
            className={`${styles.btn} ${styles.btnOutline}`}
            onClick={handleRefresh}
            type="button"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Live AI-drafting activity — stops CSRs from racing the AI with
          manual order shells; auto-refreshes the list when drafts land. */}
      <AiDraftingBanner onActivitySettled={fetchOrders} />

      {/* Search bar */}
      <div className={styles.searchBar}>
        {/*
          type="text" + role="searchbox", not type="search": the shared
          recorder's click listener treats a type="search" input as an
          actionable control and wrote an EMPTY portal_orders_search row on
          the focus click before every typed search (31 of 206 production
          rows, kanban d4dca258). A text input is skipped by that listener,
          while role="searchbox" keeps the debounced input row that carries
          the query under values.query (same pattern as sent_search and
          inbox_search).
        */}
        <input
          className={styles.searchInput}
          type="text"
          role="searchbox"
          placeholder={ORDER_SEARCH_PLACEHOLDER}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search orders"
          data-action="portal_orders_search"
          data-record-value="query"
        />
        {searchInput && (
          <button
            type="button"
            className={styles.searchClearBtn}
            onClick={() => setSearchInput('')}
            aria-label="Clear search"
            data-action="portal_orders_search_clear"
          >
            Clear
          </button>
        )}
      </div>
      {isBareOrderNumberPrefix(searchInput) && (
        <p className={styles.searchHint} role="status" data-testid="order-search-hint">
          {BARE_PREFIX_HINT}
        </p>
      )}
      {searchIsActive && !loading && matchedCustomer && (
        <p className={styles.searchCustomerBanner} role="status" data-testid="orders-search-customer">
          <span>
            {total === 1 ? 'The match is' : `All ${total} matches are`} for{' '}
            <strong>{matchedCustomer.customer_name}</strong>
            <span className={styles.searchCustomerNo}>{matchedCustomer.customer_no}</span>
          </span>
          <a
            href={`/portal/customers/${matchedCustomer.customer_no}`}
            className={styles.searchCustomerLink}
            data-testid="orders-search-customer-link"
            data-action="orders_search_customer_link"
            data-target-customer-no={matchedCustomer.customer_no}
            onClick={(e) => handleOpenMatchedCustomer(e, matchedCustomer)}
          >
            Open customer page
          </a>
        </p>
      )}

      {/* Filter Tabs */}
      <div className={styles.subFilterRow}>
        {(Object.entries(ORDER_FILTER_LABELS) as [OrderFilter, string][]).map(([filter, label]) => (
          <button
            key={filter}
            className={`${styles.subFilterBtn} ${orderFilter === filter ? styles.subFilterBtnActive : ''}`}
            onClick={() => handleFilterClick(filter)}
            type="button"
            value={filter}
            data-action="order_status_filter"
            data-record-value="filter"
            aria-pressed={orderFilter === filter}
          >
            {label}
            {filter === 'draft' && stats && stats.draft_count > 0 && (
              <span className={styles.subFilterBadge}>{stats.draft_count}</span>
            )}
            {filter === 'submission_failed' && stats && stats.submission_failed_count > 0 && (
              <span className={styles.subFilterBadge}>{stats.submission_failed_count}</span>
            )}
            {filter === 'submitted_to_sage' && stats && stats.submitted_count > 0 && (
              <span className={styles.subFilterBadge}>{stats.submitted_count}</span>
            )}
            {filter === 'accepted' && stats && stats.accepted_count > 0 && (
              <span className={styles.subFilterBadge}>{stats.accepted_count}</span>
            )}
            {filter === 'shipped' && stats && stats.shipped_count > 0 && (
              <span className={styles.subFilterBadge}>{stats.shipped_count}</span>
            )}
            {filter === 'order_completed' && stats && stats.completed_count > 0 && (
              <span className={styles.subFilterBadge}>{stats.completed_count}</span>
            )}
            {filter === 'on_hold' && stats && stats.on_hold_count > 0 && (
              <span
                className={styles.subFilterBadge}
                title={Object.entries(stats.on_hold_by_code)
                  .map(([code, n]) => `${code}: ${n}`)
                  .join(', ')}
              >
                {stats.on_hold_count}
              </span>
            )}
          </button>
        ))}
        {/* Draft-only attention sub-filter (client-side on loaded rows) */}
        {orderFilter === 'draft' && (
          <>
            <button
              type="button"
              data-testid="draft-filter-needs-attention"
              className={`${styles.subFilterBtn} ${draftAttentionFilter === 'needs_attention' ? styles.subFilterBtnActive : ''}`}
              onClick={() => handleAttentionFilterClick('needs_attention')}
              value="needs_attention"
              data-action="order_attention_filter"
              data-record-value="filter"
              title="Drafts with review items still awaiting an answer"
            >
              Needs attention
            </button>
            <button
              type="button"
              data-testid="draft-filter-ready"
              className={`${styles.subFilterBtn} ${draftAttentionFilter === 'ready' ? styles.subFilterBtnActive : ''}`}
              onClick={() => handleAttentionFilterClick('ready')}
              value="ready"
              data-action="order_attention_filter"
              data-record-value="filter"
              title="Drafts with no review items left to answer"
            >
              Ready for Sage
            </button>
            <button
              type="button"
              data-testid="draft-filter-due"
              className={`${styles.subFilterBtn} ${draftAttentionFilter === 'due' ? styles.subFilterBtnActive : ''}`}
              onClick={() => handleAttentionFilterClick('due')}
              value="due"
              data-action="order_attention_filter"
              data-record-value="filter"
              title="Drafts whose customer-requested ship or pickup date is overdue, today, or the next business day"
            >
              Due / overdue
              {dueCount > 0 && (
                <span
                  className={`${styles.subFilterBadge} ${styles.subFilterBadgeDue}`}
                  data-testid="draft-filter-due-count"
                >
                  {dueCount}
                </span>
              )}
            </button>
            <button
              type="button"
              data-testid="draft-filter-parked"
              className={`${styles.subFilterBtn} ${draftAttentionFilter === 'parked' ? styles.subFilterBtnActive : ''}`}
              onClick={() => handleAttentionFilterClick('parked')}
              value="parked"
              data-action="order_attention_filter"
              data-record-value="filter"
              title="Drafts the desk parked on purpose (waiting on the customer, credit set-up, saved for later)"
            >
              Parked
            </button>
          </>
        )}
      </div>

      {/* Which filter is on, and one click back to All (kanban 02f7bacc). */}
      {orderFilter !== 'all' && (
        <div
          className={styles.activeFilterStrip}
          data-testid="orders-active-filter"
          data-filter-source={filterSource}
          role="status"
        >
          <span>
            Showing <strong>{ORDER_FILTER_LABELS[orderFilter]}</strong> orders
            {filterSource === 'restored' && (
              <span className={styles.activeFilterNote}> (remembered from your last visit)</span>
            )}
          </span>
          <button
            type="button"
            className={styles.activeFilterClear}
            data-testid="orders-active-filter-clear"
            onClick={() => handleFilterClick('all')}
          >
            Show all orders
          </button>
        </div>
      )}

      {/* Bulk-select toolbar (visible when rows are checked) */}
      {checkedIds.size > 0 && (
        <OrdersBulkBar
          count={checkedIds.size}
          draftCount={draftSelectedCount}
          onDismissDrafts={handleBulkDismissDrafts}
          dismissing={bulkDismissing}
          onClear={handleClearSelection}
        />
      )}

      {/* Order List */}
      <div className={styles.listContainer}>
        <div className={styles.list}>
          {/* Sortable column header row */}
          <div className={styles.listHeaderRow} role="row">
            <div className={styles.listHeaderCheckbox}>
              {canManageOrders && (
                <input
                  type="checkbox"
                  aria-label="Select all on page"
                  title={
                    headerCheckState === 'all'
                      ? 'Deselect all on page'
                      : 'Select all on page'
                  }
                  checked={headerCheckState === 'all'}
                  ref={(el) => {
                    if (el) el.indeterminate = headerCheckState === 'some';
                  }}
                  onChange={handleToggleAll}
                  disabled={visibleIds.length === 0}
                />
              )}
            </div>
            <div className={styles.listHeaderLeft}>
              {renderSortLabel('Customer', 'customer_name')}
              <span className={styles.listHeaderSecondary}>
                {renderSortLabel('Date', 'created_at')}
                {renderSortLabel('Updated', 'updated_at')}
              </span>
            </div>
            <div className={styles.listHeaderRight}>
              {renderSortLabel('Subtotal', 'subtotal')}
              {renderSortLabel('Status', 'status')}
            </div>
          </div>

          {loading ? (
            <div className={styles.loadingState}>Loading orders...</div>
          ) : error ? (
            <div className={styles.errorState}>{error}</div>
          ) : visibleOrders.length === 0 ? (
            <div className={styles.emptyState}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6-2c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2z" />
              </svg>
              <p>
                {searchIsActive
                  ? `No orders match "${debouncedSearch.trim()}"`
                  : 'No orders found'}
              </p>
            </div>
          ) : (
            visibleOrders.map((order) => (
              <div
                key={order.id}
                className={`${styles.orderRow} ${dueRowAccent(order) ? styles.orderRowDue : ''} ${selectedOrderId === order.id ? styles.orderRowSelected : ''}`}
                data-due-accent={dueRowAccent(order) ? 'true' : undefined}
                onClick={() => handleSelectOrder(order.id)}
                data-action="order_open"
                data-target-order-id={order.id}
                data-outcome="open"
              >
                <div
                  className={styles.orderRowCheckbox}
                  onClick={(e) => e.stopPropagation()}
                >
                  {canManageOrders && (
                    <input
                      type="checkbox"
                      aria-label={`Select order ${order.customer_name}`}
                      checked={checkedIds.has(order.id)}
                      onChange={() => handleToggleRow(order.id)}
                    />
                  )}
                </div>
                <div className={styles.orderInfo}>
                  <div className={styles.orderCustomer}>
                    {/* The whole row, customer name included, opens the
                        order (owner-approved finding N1, review aa4424a4):
                        3 of 4 first-time explorers and 18 staff bounces in
                        90 days clicked the name expecting the order. The
                        customer page stays one click away via the small
                        link below, tracked as its own action. */}
                    <span className={styles.customerName}>{order.customer_name}</span>
                    <a
                      href={`/portal/customers/${order.customer_no}`}
                      className={styles.customerProfileLink}
                      data-testid={`orders-list-customer-link-${order.id}`}
                      title={`Open the customer page for ${order.customer_name}`}
                      aria-label={`Open the customer page for ${order.customer_name}`}
                      onClick={(e) => handleOpenCustomer(e, order)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      Customer
                    </a>
                    {order.order_type === 'B' && (
                      <span
                        data-testid="orders-list-backorder-badge"
                        title={order.backorder_of_order_id
                          ? 'Backorder — created to cover a quantity shortfall on another order'
                          : 'Backorder'}
                        style={{
                          background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d',
                          borderRadius: '4px', padding: '1px 6px', fontSize: '10px',
                          fontWeight: 700, letterSpacing: '0.4px', marginLeft: '8px',
                          verticalAlign: 'middle',
                        }}
                      >
                        BACKORDER
                      </span>
                    )}
                  </div>
                  <div className={styles.orderMeta}>
                    <OrderNumberChip order={order} />
                    <span>{order.customer_no}</span>
                    {order.po_number && <span title="Customer PO number">PO {order.po_number}</span>}
                    <span>{order.salesperson_name}</span>
                    <span>{formatDateTime(order.created_at)}</span>
                    {order.source_email_late_sync_days != null && (
                      <span
                        className={styles.lateSyncBadge}
                        data-testid="orders-list-late-sync-badge"
                        title={lateSyncTitle(
                          order.source_email_late_sync_days,
                          order.source_email_received_at,
                          order.source_email_synced_at,
                        )}
                      >
                        {formatLateSyncLabel(order.source_email_received_at, order.source_email_synced_at)}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.orderRight}>
                  <span className={styles.orderSubtotal}>
                    {formatMoney(order.subtotal)}
                    {/* The subtotal skips lines with no price; say so here
                        so a draft's amount is never read as its cost. */}
                    <UnpricedLinesMarker
                      count={order.unpriced_line_count}
                      placement="orders-list"
                      idSuffix={order.id}
                    />
                  </span>
                  <span className={`${styles.orderStatus} ${getStatusClass(order.status)}`}>
                    {ORDER_STATUS_LABELS[order.status]}
                  </span>
                  <DueBadge order={order} />
                  <ParkedBadge order={order} />
                  <AwaitingSageAccountBadge order={order} />
                  <ItemsNowInSageBadge order={order} />
                  {/* A draft with no Sage account cannot be submitted, so the
                      "Ready for Sage" chip would contradict the badge above. */}
                  {order.status === 'draft' && order.attention_count != null && !isAwaitingSageAccount(order) && (
                    order.attention_count > 0 ? (
                      <span
                        data-testid={`attention-chip-${order.id}`}
                        title={`${order.attention_count} review item${order.attention_count === 1 ? '' : 's'} awaiting an answer`}
                        style={{
                          background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d',
                          borderRadius: '4px', padding: '1px 6px', fontSize: '10px',
                          fontWeight: 700, whiteSpace: 'nowrap',
                        }}
                      >
                        {order.attention_count} to answer
                      </span>
                    ) : (
                      <span
                        data-testid={`attention-chip-${order.id}`}
                        title="No review items left to answer"
                        style={{
                          background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7',
                          borderRadius: '4px', padding: '1px 6px', fontSize: '10px',
                          fontWeight: 700, whiteSpace: 'nowrap',
                        }}
                      >
                        Ready for Sage
                      </span>
                    )
                  )}
                  {order.deleted_in_sage && (
                    <DeletedInSageBadge
                      detectedAt={order.deleted_in_sage_detected_at}
                    />
                  )}
                  <ConfidenceBadge bucket={order.confidence_bucket} prefix />

                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Order Dialog */}
      <CreateOrderDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onOrderCreated={handleOrderCreated}
      />

      {/* Order Detail Modal (replaces inline side panel) */}
      <OrderDetailModal
        isOpen={!!selectedOrderId}
        onClose={closeOrderDetail}
        orderId={selectedOrderId}
        navigation={orderNavigation}
        onOrderAction={() => {
          fetchOrders();
          fetchStats();
        }}
      />

      {/* Toast Notifications */}
      <Toast toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default PortalOrdersPage;
